// Editor.js block tool for a testimonials section: a list of quote cards
// ({ quote, attribution }) shown on the live site as a grid or a swipeable
// carousel (CSS scroll-snap — no script). Stores
//   { layout: "grid" | "carousel", items: [{ quote, attribution }] }
// Rendered by the template's render-blocks.ts.

const ICON =
  '<svg width="17" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21c3-1 4-3 4-6H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v6c0 5-3 7-8 8Z"/><path d="M14 21c3-1 4-3 4-6h-3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v6c0 5-3 7-8 8Z"/></svg>';

const LAYOUTS = [
  { value: "grid", label: "Grid" },
  { value: "carousel", label: "Carousel" },
];

const MAX_ITEMS = 12;

function cleanItems(items) {
  return (Array.isArray(items) ? items : [])
    .map((item) => ({
      quote: typeof item?.quote === "string" ? item.quote : "",
      attribution: typeof item?.attribution === "string" ? item.attribution : "",
    }))
    .slice(0, MAX_ITEMS);
}

export class TestimonialsTool {
  static get toolbox() {
    return { title: "Testimonials", icon: ICON };
  }

  constructor({ data, block }) {
    this.block = block;
    this.data = {
      layout: data?.layout === "carousel" ? "carousel" : "grid",
      items: cleanItems(data?.items),
    };
    if (this.data.items.length === 0) {
      this.data.items.push({ quote: "", attribution: "" });
    }
  }

  render() {
    const root = document.createElement("div");
    root.className = "pgs-testimonials-tool";

    const bar = document.createElement("div");
    bar.className = "pgs-gallery-tool__bar";
    const title = document.createElement("span");
    title.className = "pgs-gallery-tool__title";
    title.textContent = "Testimonials";

    const toggle = document.createElement("div");
    toggle.className = "pgs-gallery-tool__toggle";
    this._layoutButtons = LAYOUTS.map(({ value, label }) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "pgs-gallery-tool__btn";
      button.textContent = label;
      button.addEventListener("click", () => {
        this.data.layout = value;
        this._applyLayout();
        this.block?.dispatchChange?.();
      });
      toggle.appendChild(button);
      return { value, button };
    });
    this._applyLayout();

    bar.append(title, toggle);

    const list = document.createElement("div");
    list.className = "pgs-testimonials-tool__list";
    this._list = list;

    const add = document.createElement("button");
    add.type = "button";
    add.className = "pgs-gallery-tool__btn pgs-testimonials-tool__add";
    add.textContent = "+ Add testimonial";
    add.addEventListener("click", () => {
      if (this.data.items.length >= MAX_ITEMS) return;
      this.data.items.push({ quote: "", attribution: "" });
      this._renderList();
      this.block?.dispatchChange?.();
      this._list.querySelector(".pgs-testimonials-tool__row:last-child textarea")?.focus();
    });
    this._add = add;

    root.append(bar, list, add);
    this._renderList();

    return root;
  }

  _applyLayout() {
    for (const { value, button } of this._layoutButtons) {
      button.classList.toggle("pgs-gallery-tool__btn--active", this.data.layout === value);
    }
  }

  _renderList() {
    const list = this._list;
    list.innerHTML = "";

    this.data.items.forEach((item, index) => {
      const row = document.createElement("div");
      row.className = "pgs-testimonials-tool__row";

      const quote = document.createElement("textarea");
      quote.className = "pgs-testimonials-tool__quote";
      quote.rows = 2;
      quote.placeholder = "“What the customer said…”";
      quote.value = item.quote;
      quote.addEventListener("input", () => {
        item.quote = quote.value;
        this.block?.dispatchChange?.();
      });

      const meta = document.createElement("div");
      meta.className = "pgs-testimonials-tool__meta";

      const attribution = document.createElement("input");
      attribution.className = "pgs-testimonials-tool__attribution";
      attribution.type = "text";
      attribution.placeholder = "Name (optional)";
      attribution.value = item.attribution;
      attribution.addEventListener("input", () => {
        item.attribution = attribution.value;
        this.block?.dispatchChange?.();
      });

      const remove = document.createElement("button");
      remove.type = "button";
      remove.className = "pgs-gallery-tool__btn";
      remove.textContent = "Remove";
      remove.setAttribute("aria-label", `Remove testimonial ${index + 1}`);
      remove.addEventListener("click", () => {
        this.data.items.splice(index, 1);
        if (this.data.items.length === 0) this.data.items.push({ quote: "", attribution: "" });
        this._renderList();
        this.block?.dispatchChange?.();
      });

      meta.append(attribution, remove);
      row.append(quote, meta);
      list.appendChild(row);
    });

    this._add?.classList.toggle("pgs-testimonials-tool__add--hidden", this.data.items.length >= MAX_ITEMS);
  }

  save() {
    return {
      layout: this.data.layout,
      items: this.data.items
        .map((item) => ({ quote: item.quote.trim(), attribution: item.attribution.trim() }))
        .filter((item) => item.quote),
    };
  }
}
